import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  Search, Mail, Phone, CheckCircle2, ArrowUpRight, CalendarClock, BarChart3, PieChart,
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip as ReTooltip, ResponsiveContainer } from "recharts";
import api from "@/lib/api";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { cn, initialsOf, dateLocale } from "@/lib/utils";
import { useLang, translateFr as tfr } from "@/i18n";

const refreshNotifsGlobal = () => window.dispatchEvent(new Event("athlynx:notifs-refresh"));

const TABS = [
  { id: "overdue", label: tfr("En retard") },
  { id: "today", label: tfr("Aujourd'hui") },
  { id: "upcoming", label: tfr("À venir") },
  { id: "done", label: "Faites" },
];
const PERIODS = [
  { v: "24h", l: "24h" }, { v: "7j", l: "7j" }, { v: "30j", l: "30j" },
  { v: "12m", l: "12m" }, { v: "all", l: "All" },
];
const CHANNEL_FR = { email: "Email", phone: tfr("Téléphone"), whatsapp: "WhatsApp", message: "Message", meeting: tfr("Réunion") };

export function relativeAgo(iso) {
  if (!iso) return "—";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff <= 5) return tfr("à l'instant");
  if (diff < 60) return dateLocale() === "en-GB" ? `${diff}s ago` : `il y a ${diff} s`;
  if (diff < 3600) return dateLocale() === "en-GB" ? `${Math.floor(diff / 60)} min ago` : `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return `il y a ${Math.floor(diff / 86400)} j`;
}

function dueLabel(fu, tab) {
  if (tab === "overdue") return `${tfr("En retard de")} ${fu.days_overdue} ${fu.days_overdue > 1 ? tfr("jours") : tfr("jour")}`;
  if (tab === "today") return "Aujourd'hui";
  const d = new Date(fu.due_at);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((new Date(d.toDateString()) - today) / 864e5);
  if (days === 1) return "Demain";
  if (days > 1 && days <= 7) return `${tfr("Dans")} ${days} ${tfr("jours")}`;
  return d.toLocaleDateString(dateLocale(), { day: "numeric", month: "short", year: "numeric" });
}

function fmtDone(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const today = new Date().toDateString() === d.toDateString();
  const time = d.toLocaleTimeString(dateLocale(), { hour: "2-digit", minute: "2-digit" });
  return today ? `${tfr("Fait aujourd'hui à")} ${time}` : `${tfr("Fait le")} ${d.toLocaleDateString(dateLocale(), { day: "numeric", month: "short" })} ${tfr("à")} ${time}`;
}

export default function Relances() {
  const { tf } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "overdue";
  const [period, setPeriod] = useState("7j");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = useCallback(async (p) => {
    try {
      const r = await api.get(`/relances?period=${p || period}`);
      setData(r.data);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  useEffect(() => { load(period); }, [period, load]);

  const refresh = useCallback(() => { load(period); refreshNotifsGlobal(); }, [load, period]);

  const counts = data?.counts || {};
  const setTab = (t) => setSearchParams(t === "overdue" ? {} : { tab: t }, { replace: true });

  const list = useMemo(() => {
    const raw = data?.tabs?.[tab] || [];
    if (!q.trim()) return raw;
    const ql = q.toLowerCase();
    return raw.filter((c) => {
      const p = c.player || {};
      const reason = (c.primary || c.followup)?.note || "";
      return (p.name || "").toLowerCase().includes(ql) || (p.club || "").toLowerCase().includes(ql)
        || reason.toLowerCase().includes(ql);
    });
  }, [data, tab, q]);

  // hoisted at page level so it survives list refresh after completion
  const [nextDlg, setNextDlg] = useState(null);
  const [cadencePlayer, setCadencePlayer] = useState(null);

  return (
    <div className="px-4 md:px-6 lg:px-8 py-5 max-w-[1200px] mx-auto pb-12 animate-fade-in" data-testid="relances-page">
      <header>
        <h1 className="text-3xl md:text-[34px] font-bold tracking-tight" data-testid="relances-title">{tf("À relancer")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{tf("Suivez vos relances et gardez chaque échange au bon moment.")}</p>
      </header>

      {/* Operational tabs */}
      <div className="mt-4 flex gap-1.5 overflow-x-auto tabstrip-x" role="tablist" data-testid="relances-tabs">
        {TABS.map((t) => {
          const tone = { overdue: "border-athlynx/40 bg-athlynx/10", today: "border-warning/40 bg-warning/10", upcoming: "border-info/40 bg-info/10", done: "border-success/40 bg-success/10" }[t.id] || "border-foreground/25 bg-secondary";
          return (
            <button key={t.id} role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
              data-testid={`relances-tab-${t.id}`}
              className={cn("shrink-0 rounded-lg border px-3 py-1.5 text-sm font-medium inline-flex items-center gap-2 transition-colors",
                tab === t.id ? `${tone} text-foreground` : "border-border text-muted-foreground hover:text-foreground hover:bg-secondary/60")}>
              {tf(t.label)}
              <span className="tabular-nums rounded-full border border-border px-1.5 py-px text-[11px]">
                {counts[t.id] ?? 0}
              </span>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative mt-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={tf("Rechercher un athlète, un club, un motif…")}
          className="pl-9 h-10 rounded-xl border-border bg-card text-sm" data-testid="relances-search" />
      </div>

      {/* Player list */}
      <div className="mt-4 space-y-2" data-testid="relances-list">
        {loading ? (
          [0, 1, 2].map((i) => <div key={i} className="h-[88px] rounded-2xl bg-secondary/60 animate-pulse" />)
        ) : list.length === 0 ? (
          <EmptyState tab={tab} tf={tf} />
        ) : tab === "done" ? (
          list.map((e, i) => <DoneRow key={`${e.followup.id}-${i}`} entry={e} tf={tf} />)
        ) : (
          list.map((c) => <PlayerRow key={c.player.id} card={c} tab={tab} tf={tf} onChanged={refresh}
            onCompleted={(info) => setNextDlg(info)} onEditCadence={setCadencePlayer} />)
        )}
      </div>

      <NextFollowUpDialog open={!!nextDlg} onOpenChange={(v) => !v && setNextDlg(null)}
        player={nextDlg?.player} defaultNote={nextDlg?.defaultNote} tf={tf} onSaved={refresh} />

      <CadenceDialog open={!!cadencePlayer} onOpenChange={(v) => !v && setCadencePlayer(null)}
        player={cadencePlayer} tf={tf} onSaved={refresh} />

      {/* Analytics — period-scoped, never filters the lists above */}
      <AnalyticsSection data={data} period={period} setPeriod={setPeriod} tf={tf} />

      <div className="mt-4 text-[11px] text-muted-foreground flex items-center gap-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-success" /> {tf("Mis à jour")} {relativeAgo(data?.updated_at)}
      </div>
    </div>
  );
}

function EmptyState({ tab, tf }) {
  const msg = { overdue: tfr("Aucune relance en retard."), today: tfr("Aucune relance prévue aujourd'hui."),
    upcoming: tfr("Aucune relance à venir."), done: tfr("Aucune relance terminée.") }[tab];
  return (
    <div className="rounded-2xl border border-border bg-card px-5 py-10 text-center text-sm text-muted-foreground"
      data-testid={`relances-empty-${tab}`}>
      {tf(msg)}
    </div>
  );
}

function PlayerRow({ card, tab, tf, onChanged, onCompleted, onEditCadence }) {
  const p = card.player;
  const fu = card.primary;
  const [confirm, setConfirm] = useState(null); // channel pending confirmation
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const channel = fu.channel || (p.email ? "email" : p.phone ? "phone" : null);

  const complete = async (ch) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = fu.id
        ? await api.post(`/relances/followups/${fu.id}/complete`, { channel: ch || channel })
        : await api.post(`/relances/players/${p.id}/complete`, { channel: ch || channel });
      const undoId = fu.id || r.data.id;
      if (!r.data.already) {
        toast.success(tf("Relance marquée comme faite."), {
          action: {
            label: tf("Annuler"),
            onClick: async () => { await api.post(`/relances/followups/${undoId}/undo`); onChanged?.(); },
          },
        });
        onCompleted?.({ player: p, defaultNote: fu.scheduled ? "" : fu.note });
      }
      setConfirm(null);
      onChanged?.();
    } catch { toast.error(tf("Erreur")); } finally { setBusy(false); }
  };

  const confirmQuestion = { email: tfr("Relance envoyée ?"), phone: tfr("Appel effectué ?"), whatsapp: tfr("Message envoyé ?"), message: tfr("Message envoyé ?") }[confirm] || "Relance envoyée ?";

  return (
    <div className="rounded-2xl border border-border dark:border-white/[0.08] bg-card p-3.5" data-testid={`relance-row-${p.id}`}>
      <div className="flex items-start gap-3">
        <Avatar className="h-10 w-10 shrink-0">
          <AvatarImage src={p.photo_url || undefined} alt="" />
          <AvatarFallback className="text-xs font-semibold bg-secondary">{initialsOf(p.name)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/players/${p.id}`} className="text-sm font-semibold hover:underline truncate" data-testid={`relance-player-${p.id}`}>{p.name}</Link>
            {p.status && (
              <span className="text-[10px] font-medium rounded-full border border-border px-2 py-0.5 text-muted-foreground">{tf(p.status)}</span>
            )}
          </div>
          <div className="text-xs text-muted-foreground truncate mt-0.5">
            {[p.club, p.position].filter(Boolean).join(" · ") || "—"}
          </div>
          <div className="mt-1.5 text-sm break-words" data-testid={`relance-reason-${p.id}`}>
            {fu.note || tf("Motif non renseigné")}
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-1">
            <span className={cn("text-xs font-medium", tab === "overdue" ? "text-athlynx" : tab === "today" ? "text-warning" : "text-muted-foreground")}>
              {dueLabel(fu, tab)}
            </span>
            <span className="text-[11px] text-muted-foreground">
              {tf("Canal")} : {channel ? tf(CHANNEL_FR[channel] || channel) : tf("Canal non défini")}
            </span>
            {p.follow_up && (
              <button onClick={() => onEditCadence?.(p)} data-testid={`relance-cadence-${p.id}`}
                className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline">
                {tf("Fréquence de suivi")} : {p.follow_up.cadence_days} {tf("jours")}
                {p.follow_up.custom_cadence && <span className="ml-1 text-info">· {tf("Personnalisée")}</span>}
              </button>
            )}
          </div>
          {card.others?.length > 0 && (
            <button onClick={() => setExpanded((v) => !v)} className="mt-1.5 text-[11px] text-info hover:underline"
              data-testid={`relance-expand-${p.id}`}>
              {expanded ? tf("Masquer") : `+ ${card.others.length} ${tf("autre(s) relance(s)")}`}
            </button>
          )}
          {expanded && (
            <ul className="mt-1.5 space-y-1 border-l-2 border-border pl-2.5">
              {card.others.map((o) => (
                <li key={o.id} className="text-xs text-muted-foreground">
                  {o.note || tf("Motif non renseigné")} · {dueLabel(o, tab)}
                </li>
              ))}
            </ul>
          )}
        </div>
        {/* Quick actions */}
        <div className="flex items-center gap-0.5 shrink-0">
          {p.email && (
            <a href={`mailto:${p.email}?subject=${encodeURIComponent(fu.note || "Suivi")}`}
              onClick={() => setConfirm("email")} aria-label={tf("Envoyer un email")}
              className="p-2 rounded-lg text-info hover:bg-info/10" data-testid={`relance-email-${p.id}`}>
              <Mail className="h-4 w-4" />
            </a>
          )}
          {p.phone && (
            <a href={`tel:${p.phone}`} onClick={() => setConfirm("phone")} aria-label={tf("Appeler")}
              className="p-2 rounded-lg text-info hover:bg-info/10" data-testid={`relance-phone-${p.id}`}>
              <Phone className="h-4 w-4" />
            </a>
          )}
          <button onClick={() => complete(null)} disabled={busy} aria-label={tf("Marquer comme faite")}
            title={tf("Marquer comme faite")}
            className="p-2 rounded-lg text-success hover:bg-success/10" data-testid={`relance-complete-${p.id}`}>
            <CheckCircle2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Post-communication confirmation — ONLY "Oui" completes the follow-up */}
      {confirm && (
        <div className="mt-3 rounded-xl border border-info/25 bg-info/[0.05] px-3 py-2.5 flex items-center justify-between gap-3 flex-wrap"
          data-testid={`relance-confirm-${p.id}`}>
          <span className="text-sm font-medium">{tf(confirmQuestion)}</span>
          <span className="flex gap-2">
            <Button size="sm" onClick={() => complete(confirm)} disabled={busy}
              className="h-8 bg-info hover:bg-info/90 text-white" data-testid={`relance-confirm-yes-${p.id}`}>
              {tf("Oui")}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirm(null)}
              className="h-8" data-testid={`relance-confirm-no-${p.id}`}>
              {tf("Pas encore")}
            </Button>
          </span>
        </div>
      )}
    </div>
  );
}

function NextFollowUpDialog({ open, onOpenChange, player, defaultNote, tf, onSaved }) {
  const [due, setDue] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      const d = new Date(); d.setDate(d.getDate() + 5);
      setDue(d.toISOString().slice(0, 10));
      setNote(defaultNote || "");
    }
  }, [open, defaultNote]);
  const save = async () => {
    if (!due) return;
    setBusy(true);
    try {
      await api.post(`/relances/followups`, { player_id: player.id, due_at: due, note });
      toast.success(tf("Prochaine relance programmée"));
      onOpenChange(false); onSaved?.();
    } catch { toast.error(tf("Erreur")); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]" data-testid="next-followup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <CalendarClock className="h-4 w-4" /> {tf("Programmer la prochaine relance ?")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">{player?.name} · {tf("suggestion")} J+5 — {tf("modifiable")}</p>
          <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="next-followup-date" />
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={tf("Motif")} data-testid="next-followup-note" />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="next-followup-skip">{tf("Pas maintenant")}</Button>
          <Button onClick={save} disabled={busy} data-testid="next-followup-save">{tf("Programmer")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CadenceDialog({ open, onOpenChange, player, tf, onSaved }) {
  const [days, setDays] = useState("");
  const [busy, setBusy] = useState(false);
  const fu = player?.follow_up;
  useEffect(() => { if (open && fu) setDays(String(fu.cadence_days || "")); }, [open, fu]);
  const save = async (value) => {
    setBusy(true);
    try {
      await api.patch(`/relances/players/${player.id}/cadence`, { cadence_days: value });
      toast.success(value == null ? tf("Délai par défaut rétabli") : tf("Fréquence de suivi mise à jour"));
      onOpenChange(false); onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || tf("Erreur"));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]" data-testid="cadence-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <CalendarClock className="h-4 w-4" /> {tf("Fréquence de suivi")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            {player?.name} · {tf("Délai par défaut pour")} « {tf(player?.status || "Repéré")} » : {fu?.default_cadence_days} {tf("jours")}
          </p>
          <div className="flex items-center gap-2">
            <Input type="number" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)}
              className="w-24" data-testid="cadence-input" />
            <span className="text-sm text-muted-foreground">{tf("jours")}</span>
          </div>
        </div>
        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" onClick={() => save(null)} disabled={busy} data-testid="cadence-reset">
            {tf("Utiliser le délai par défaut")}
          </Button>
          <Button onClick={() => save(parseInt(days, 10))} disabled={busy || !days} data-testid="cadence-save">
            {tf("Enregistrer")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DoneRow({ entry, tf }) {
  const p = entry.player;
  const fu = entry.followup;
  return (
    <div className="rounded-2xl border border-border dark:border-white/[0.08] bg-card p-3.5 flex items-start gap-3"
      data-testid={`done-row-${fu.id}`}>
      <Avatar className="h-9 w-9 shrink-0">
        <AvatarImage src={p.photo_url || undefined} alt="" />
        <AvatarFallback className="text-xs font-semibold bg-secondary">{initialsOf(p.name)}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/players/${p.id}`} className="text-sm font-semibold hover:underline truncate">{p.name}</Link>
          <span className="text-xs text-muted-foreground truncate">{fu.note || tf("Motif non renseigné")}</span>
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">
          {fmtDone(fu.completed_at)}
          {fu.completed_channel && ` · ${tf(CHANNEL_FR[fu.completed_channel] || fu.completed_channel)}`}
          {fu.completed_by && ` · ${fu.completed_by}`}
        </div>
      </div>
      <Link to={`/players/${p.id}`} className="shrink-0 p-1.5 rounded-md text-muted-foreground hover:text-foreground"
        aria-label={tf("Ouvrir la fiche athlète")}>
        <ArrowUpRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

function AnalyticsSection({ data, period, setPeriod, tf }) {
  const a = data?.analytics;
  const reducedMotion = typeof window !== "undefined"
    && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

  const fmtTick = (iso) => {
    const d = new Date(iso);
    if (period === "12m" || period === "all") {
      return d.toLocaleDateString(dateLocale(), { month: "short", year: "2-digit" });
    }
    return d.toLocaleDateString(dateLocale(), { day: "numeric", month: "short" });
  };

  return (
    <section className="mt-8" data-testid="relances-analytics">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-base font-semibold flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-muted-foreground" /> {tf("Analyse des relances")}
        </h2>
        <div className="flex flex-wrap gap-1 rounded-full border border-border bg-card p-0.5"
          role="group" aria-label={tf("Période des statistiques")}>
          {PERIODS.map((p) => (
            <button key={p.v} onClick={() => setPeriod(p.v)} data-testid={`relances-period-${p.v}`}
              aria-pressed={period === p.v}
              className={cn("rounded-full px-3 py-1 text-xs font-medium transition-colors",
                period === p.v ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground")}>
              {p.l}
            </button>
          ))}
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground mt-1">
        {tf("La période ne s'applique qu'aux statistiques ci-dessous — jamais aux listes opérationnelles.")}
      </p>
      {a && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
          <div className="min-w-0 rounded-2xl border border-border dark:border-white/[0.08] bg-card p-4"
            role="img" aria-label={`${tf("Relances complétées")} : ${a.completed_current} ${tf("sur la période")} ${a.period_label}`}>
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {tf("Relances complétées")} — {tf("évolution sur")} {a.period_label}
              </div>
              <div className={cn("text-xs",
                a.delta == null ? "text-muted-foreground"
                  : a.delta > 0 ? "text-success" : a.delta < 0 ? "text-athlynx" : "text-muted-foreground")}>
                {a.delta == null ? tf("Données insuffisantes")
                  : a.delta === 0 ? `${tf("Stable vs période précédente")}`
                  : `${a.delta > 0 ? "+" : ""}${a.delta} ${tf("vs période précédente")}`}
              </div>
            </div>
            <div className="text-2xl font-bold mt-1 tabular-nums" data-testid="analytics-completed-count">{a.completed_current}</div>
            {a.completed_current === 0 && (
              <p className="text-xs text-muted-foreground mt-0.5" data-testid="analytics-empty">
                {tf("Aucune relance complétée sur cette période.")}
              </p>
            )}
            <div className="h-32 mt-2 -mx-1">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={a.timeseries} margin={{ top: 6, right: 8, bottom: 0, left: 8 }}>
                  <defs>
                    <linearGradient id="relGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#e11d48" stopOpacity={0.22} />
                      <stop offset="100%" stopColor="#e11d48" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tickFormatter={fmtTick} interval="preserveStartEnd"
                    minTickGap={28} axisLine={false} tickLine={false}
                    tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis hide allowDecimals={false} domain={[0, (max) => Math.max(2, max)]} />
                  <ReTooltip
                    content={<ChartTooltip series={a.timeseries} tf={tf} />}
                    cursor={{ stroke: "hsl(var(--border))", strokeDasharray: "3 3", strokeWidth: 1 }}
                    wrapperStyle={{ outline: "none", zIndex: 20 }}
                    allowEscapeViewBox={{ x: false, y: false }}
                  />
                  <Area type="monotone" dataKey="value" stroke="#e11d48" strokeWidth={2}
                    fill="url(#relGrad)" isAnimationActive={!reducedMotion}
                    dot={{ r: 2.5, fill: "#e11d48", strokeWidth: 0 }}
                    activeDot={{ r: 5, fill: "#e11d48", stroke: "hsl(var(--card))", strokeWidth: 2 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="min-w-0 rounded-2xl border border-border dark:border-white/[0.08] bg-card p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
              <PieChart className="h-3.5 w-3.5" /> {tf("Répartition par statut dans le pipeline")}
            </div>
            <ul className="mt-3 space-y-2">
              {(a.distribution || []).map((d) => (
                <li key={d.status} className="flex items-center gap-2 text-sm">
                  <span className="w-28 shrink-0 text-xs text-muted-foreground">{tf(d.status)}</span>
                  <span className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
                    <span className="block h-full rounded-full bg-info/70" style={{ width: `${d.pct}%` }} />
                  </span>
                  <span className="w-8 text-right text-xs tabular-nums">{d.count}</span>
                </li>
              ))}
            </ul>
            <div className="text-[11px] text-muted-foreground mt-2">{a.total_players} {tf("athlètes au total")}</div>
          </div>
        </div>
      )}
    </section>
  );
}

function ChartTooltip({ active, payload, label, series, tf }) {
  if (!active || !payload?.length) return null;
  const value = payload[0]?.value ?? 0;
  const dup = (series || []).filter((s) => s.date === label).length > 1;
  const idx = (series || []).findIndex((s) => s.date === label);
  const prev = !dup && idx > 0 ? series[idx - 1] : null;
  const d = new Date(label);
  const dateFull = d.toLocaleDateString(dateLocale(), { day: "numeric", month: "long", year: "numeric" });
  let compare = null;
  if (prev) {
    const diff = value - prev.value;
    const prevDate = new Date(prev.date);
    const sameDay = prev.date === label;
    const prevLabel = sameDay ? tf("point précédent")
      : prevDate.toLocaleDateString(dateLocale(), { day: "numeric", month: "long" });
    compare = { diff, prevLabel };
  }
  return (
    <div className="max-w-[220px] rounded-xl border border-border dark:border-white/[0.1] bg-card dark:bg-[#15181F] px-3.5 py-3 shadow-lg"
      data-testid="relances-chart-tooltip" role="status">
      <div className="text-[11px] text-muted-foreground">{dateFull}</div>
      <div className="mt-0.5 flex items-baseline gap-1.5">
        <span className="text-xl font-bold text-foreground tabular-nums">{value}</span>
        <span className="text-sm">{value > 1 ? tf("relances complétées") : tf("relance complétée")}</span>
      </div>
      {compare && (
        <div className={cn("text-[11px] mt-1",
          compare.diff > 0 ? "text-success" : compare.diff < 0 ? "text-athlynx" : "text-muted-foreground")}>
          {compare.diff === 0 ? `${tf("Stable vs")} ${compare.prevLabel}`
            : `${compare.diff > 0 ? "+" : ""}${compare.diff} vs ${compare.prevLabel}`}
        </div>
      )}
    </div>
  );
}
